import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiList, FiFilter, FiClock, FiUser, FiMessageSquare, FiCheck, FiX } = FiIcons;

const Bookings = () => {
  const [viewMode, setViewMode] = useState('list');
  const [filter, setFilter] = useState('upcoming');

  const bookings = [
    {
      id: 1,
      client: 'Sara Johnson',
      service: 'Home Cleaning',
      date: '2024-01-15',
      time: '10:00 AM',
      status: 'confirmed',
      price: '$120',
      duration: '3 hours'
    },
    {
      id: 2,
      client: 'Mike Chen',
      service: 'Garden Maintenance',
      date: '2024-01-16',
      time: '2:00 PM',
      status: 'pending',
      price: '$85',
      duration: '2 hours'
    },
    {
      id: 3,
      client: 'Lisa Rodriguez',
      service: 'Pet Sitting',
      date: '2024-01-17',
      time: '9:00 AM',
      status: 'confirmed',
      price: '$60',
      duration: '4 hours'
    },
    {
      id: 4,
      client: 'David Wilson',
      service: 'Tutoring',
      date: '2024-01-18',
      time: '4:00 PM',
      status: 'rescheduled',
      price: '$45',
      duration: '1 hour'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rescheduled': return 'bg-blue-100 text-blue-800';
      case 'canceled': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'upcoming') return ['confirmed', 'pending'].includes(booking.status);
    if (filter === 'past') return booking.status === 'completed';
    if (filter === 'canceled') return booking.status === 'canceled';
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <motion.div 
        className="flex items-center justify-between"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Bookings</h1>
          <p className="text-slate-600 mt-1">See and manage all appointments</p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div className="flex bg-white rounded-lg border border-slate-200 p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all ${
                viewMode === 'list' 
                  ? 'bg-trust-500 text-white' 
                  : 'text-slate-600 hover:text-trust-600'
              }`}
            >
              <SafeIcon icon={FiList} />
              List
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all ${
                viewMode === 'calendar' 
                  ? 'bg-trust-500 text-white' 
                  : 'text-slate-600 hover:text-trust-600'
              }`}
            >
              <SafeIcon icon={FiCalendar} />
              Calendar
            </button>
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div 
        className="flex items-center gap-4 bg-white rounded-xl p-4 border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <SafeIcon icon={FiFilter} className="text-slate-500" />
        <div className="flex gap-2">
          {['upcoming', 'past', 'canceled'].map((filterType) => (
            <button
              key={filterType}
              onClick={() => setFilter(filterType)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === filterType
                  ? 'bg-trust-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.map((booking, index) => (
          <motion.div
            key={booking.id}
            className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-trust-400 to-trust-500 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiUser} className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{booking.client}</h3>
                  <p className="text-slate-600">{booking.service}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="flex items-center gap-2 text-slate-600">
                    <SafeIcon icon={FiCalendar} className="text-sm" />
                    <span>{booking.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <SafeIcon icon={FiClock} className="text-sm" />
                    <span>{booking.time} ({booking.duration})</span>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-lg font-semibold text-slate-800">{booking.price}</div>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <motion.button
                    className="p-2 bg-trust-100 text-trust-600 rounded-lg hover:bg-trust-200 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiMessageSquare} />
                  </motion.button>
                  <motion.button
                    className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiCheck} />
                  </motion.button>
                  <motion.button
                    className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiX} />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredBookings.length === 0 && (
        <motion.div 
          className="text-center py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <SafeIcon icon={FiCalendar} className="text-6xl text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-600 mb-2">No bookings found</h3>
          <p className="text-slate-500">Try adjusting your filters or check back later.</p>
        </motion.div>
      )}
    </div>
  );
};

export default Bookings;